import { useCallback, useContext, useEffect } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { QrCodeActions, ResponseContext, TableContext } from "@swiggy-private/portal-dineout-context";
import type { ICartItems } from "@swiggy-private/portal-dineout-context";

import { CROSS_SELL_DEFAULT_FREQUENCY_CAP } from "../constants/menu";
import { STORAGE_KEYS } from "../constants/StorageKeys";
import { getCrossSellShownItemIds, updateCrossSellShownItemIds } from "../utils/frequencyCapping";

type CartItemWithUUID = ICartItems & { menu_item_uuid: string };

export const useCrossSellActions = () => {
    const {
        state: { crossSell, cart },
        dispatch,
    } = useContext(ResponseContext);
    const { tableId = "" } = useContext(TableContext);
    const { config, shownItemIds } = crossSell;

    // On mount or tableId change, load shown items from storage into context.
    // A new tableId or an expired TTL returns an empty list, resetting the cap.
    useEffect(() => {
        (async () => {
            const stored = await getCrossSellShownItemIds(STORAGE_KEYS.CROSS_SELL_FREQ, tableId);
            dispatch({
                type: QrCodeActions.CROSS_SELL_SET_SHOWN_ITEMS,
                payload: { shownItemIds: stored },
            });
        })();
    }, [tableId, dispatch]);

    const handleCrossSellOnCartUpdate = useCallback(
        (
            newItem: CartItemWithUUID,
            previousQuantity: number,
            options: { isCrossSellItem?: boolean; cartItemIdsAtOpen?: string[] } = {},
        ) => {
            const { isCrossSellItem = false, cartItemIdsAtOpen: cartItemIdsAtOpenOpt } = options;
            const cartItemIdsAtOpen =
                cartItemIdsAtOpenOpt ?? (cart.localCart?.cartItems ?? []).map((i) => i.menu_item_id);
            if (isEmpty(newItem.quantity)) {
                dispatch({
                    type: QrCodeActions.CROSS_SELL_REMOVE_ENTRY,
                    payload: { itemId: newItem.menu_item_id },
                });
                return;
            }
            const crossSellMap = config?.crossSell?.crossSellPerItem?.crossSellMap;
            if (
                newItem.quantity > previousQuantity &&
                !isCrossSellItem &&
                crossSellMap?.[newItem.menu_item_id]?.rawChildItems?.length
            ) {
                const isNewItem = !shownItemIds.includes(newItem.menu_item_id);
                const underCap = shownItemIds.length < (config?.frequencyCapping ?? CROSS_SELL_DEFAULT_FREQUENCY_CAP);
                if (!isNewItem || underCap) {
                    dispatch({
                        type: QrCodeActions.CROSS_SELL_ADD_ENTRY,
                        payload: {
                            itemId: newItem.menu_item_id,
                            itemName: newItem.name,
                            cartItemIdsAtOpen,
                        },
                    });
                }
            }
        },
        [dispatch, config, shownItemIds, cart.localCart],
    );

    // Called by CrossSellContent once items are confirmed visible.
    // Only then do we consume a frequency cap slot.
    const onCrossSellShown = useCallback(
        (itemId: string) => {
            if (shownItemIds.includes(itemId)) return;
            const updatedIds = [...shownItemIds, itemId];
            dispatch({ type: QrCodeActions.CROSS_SELL_SET_SHOWN_ITEMS, payload: { shownItemIds: updatedIds } });
            updateCrossSellShownItemIds(STORAGE_KEYS.CROSS_SELL_FREQ, tableId, updatedIds);
        },
        [dispatch, shownItemIds, tableId],
    );

    return { handleCrossSellOnCartUpdate, onCrossSellShown };
};
